import { z } from "zod";
import { businessIdParamSchema, isoDateSchema, isoDateTimeSchema, uuidSchema } from "./common.ts";
import { familyInviteStateSchema } from "./families.ts";
import { messageBodySchema } from "./messaging.ts";

/**
 * The Clients table (Pilates studio shape, C1): one row for each client of a business where
 * clients book for themselves. The pack, the sessions left, the end date, the last visit, the next
 * booking and three flags. The server filters the list and counts every filter.
 *
 * Health answers (C2) never go in a row or in the export: they are on the client page only.
 */

export const clientFilters = ["all", "active", "ending_soon", "intro", "quiet"] as const;
export const clientFilterSchema = z.enum(clientFilters);
export type ClientFilter = z.infer<typeof clientFilterSchema>;

export const clientRowSchema = z.object({
  /** The client's account (`Family.id`): the address of the client page. */
  familyId: uuidSchema,
  /** The client's own participant here. Null until the client has one (a number on the list). */
  participantId: uuidSchema.nullable(),
  name: z.string(),
  phoneE164: z.string().nullable(),
  invite: familyInviteStateSchema,
  /** The pack the row shows: of the current packs, the one with sessions left that ends last. */
  packageName: z.string().nullable(),
  /** Sessions left on every current pack together. Null with no current pack. */
  sessionsLeft: z.number().int().min(0).nullable(),
  /** The last day of the pack the row shows, in the business timezone. */
  endsOn: isoDateSchema.nullable(),
  /** The start of the last session the client came to (booked, started, not marked absent). */
  lastVisitAt: isoDateTimeSchema.nullable(),
  /** The start of the next session the client is booked into. */
  nextBookingAt: isoDateTimeSchema.nullable(),
  /** A current pack is an intro offer (`newClientsOnly`, M1). */
  intro: z.boolean(),
  /** A current pack is paused today (M3). */
  paused: z.boolean(),
  /** A pack is not paid yet. */
  owed: z.boolean(),
});
export type ClientRow = z.infer<typeof clientRowSchema>;

export const clientCountsSchema = z.object(
  Object.fromEntries(clientFilters.map((filter) => [filter, z.number().int().min(0)])) as Record<ClientFilter, z.ZodNumber>,
);
export type ClientCounts = Record<ClientFilter, number>;

export const clientsParamSchema = businessIdParamSchema;
export const clientsQuerySchema = z.object({ filter: clientFilterSchema.default("all") });
export type ClientsQuery = z.infer<typeof clientsQuerySchema>;

export const clientsResponseSchema = z.object({
  filter: clientFilterSchema,
  /** Today in the business timezone, the day the filters were read on. */
  today: isoDateSchema,
  timezone: z.string(),
  items: z.array(clientRowSchema),
  /** How many clients each filter holds, so the chips say it before a tap. */
  counts: clientCountsSchema,
});
export type ClientsResponse = z.infer<typeof clientsResponseSchema>;

/** At most this many clients in one message to many: one owner, one hour (the messaging limit is lower). */
export const MAX_CLIENTS_PER_MESSAGE = 500;

/**
 * `POST /business/businesses/:id/clients/message`: one message into the private thread of each
 * client. Name the rows (`familyIds`) or a whole filter (`filter`), not both. Never a WhatsApp group.
 */
export const clientsMessageRequestSchema = z
  .object({
    familyIds: z.array(uuidSchema).min(1).max(MAX_CLIENTS_PER_MESSAGE).optional(),
    filter: clientFilterSchema.optional(),
    body: messageBodySchema,
  })
  .refine((value) => (value.familyIds === undefined) !== (value.filter === undefined), {
    message: "Choose the rows or a filter, not both.",
    path: ["familyIds"],
  });
export type ClientsMessageRequest = z.infer<typeof clientsMessageRequestSchema>;

export const clientsMessageResponseSchema = z.object({
  /** The clients the message was for. */
  recipients: z.number().int().min(0),
  /** The clients who got it in their thread. */
  sent: z.number().int().min(0),
  /** Not in the app yet (no active membership): they got nothing. */
  notJoined: z.number().int().min(0),
  /** Not sent because the hourly message limit was reached. Send again later. */
  limited: z.number().int().min(0),
});
export type ClientsMessageResponse = z.infer<typeof clientsMessageResponseSchema>;
